import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Boxes, Refrigerator, Wine, Search, Plus, Minus, MoreVertical, Printer, CheckCircle2, Thermometer, ShoppingCart, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { downloadPdf } from "../lib/pdf";
import { PdfSheet, PdfSection, PdfTable } from "../lib/PdfSheet";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const w = x.getDay(); x.setDate(x.getDate() + (w === 0 ? -6 : 1 - w)); x.setHours(0, 0, 0, 0); return x; };
const fmt = (s) => { const d = new Date(s + "T00:00:00"); return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`; };
const OBS = ["RAS", "Stock faible", "Stock critique", "Rupture", "À commander rapidement", "Commande passée", "Livraison attendue", "Produit bientôt périmé", "Produit périmé", "Date à contrôler", "Emballage endommagé", "Produit ouvert", "À utiliser en priorité", "Surstock", "Emplacement à vérifier", "Erreur de livraison", "Retour fournisseur", "Prévoir pour événement"];
const CATS = {
  sec: { icon: Boxes, color: "#0d9488", bg: "linear-gradient(135deg,#134e4a,#0d9488)", wood: "#8a5a2b" },
  frais: { icon: Refrigerator, color: "#0284c7", bg: "linear-gradient(135deg,#0c4a6e,#0284c7)", wood: "#94a3b8" },
  boissons: { icon: Wine, color: "#7c3aed", bg: "linear-gradient(135deg,#4c1d95,#7c3aed)", wood: "#8a5a2b" },
};
const statusOf = (p, c) => {
  if (c === undefined || c === null) return { k: "acompter", col: "#64748b", bg: "#e2e8f0" };
  if (c.qty === 0) return { k: "rupture", col: "#dc2626", bg: "#fee2e2" };
  if (c.qty < p.stock_min) return { k: "acommander", col: "#ea580c", bg: "#ffedd5" };
  if (c.qty === p.stock_min) return { k: "aumini", col: "#ca8a04", bg: "#fef9c3" };
  return { k: "ok", col: "#16a34a", bg: "#dcfce7" };
};
const emptyProd = { id: null, name: "", category: "sec", cond: "", unit: "", stock_min: 1, shelf: 1, image_url: "", notes: "", dlc: "" };

export default function CuisineInventaire() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [st, setSt] = useState(null);
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tous");
  const [countP, setCountP] = useState(null);
  const [qty, setQty] = useState(0);
  const [obs, setObs] = useState("");
  const [prodForm, setProdForm] = useState(null);
  const [drag, setDrag] = useState(null);
  const [moveAsk, setMoveAsk] = useState(null);
  const [tempOpen, setTempOpen] = useState(false);
  const [tempVal, setTempVal] = useState("");
  const [summary, setSummary] = useState(false);
  const ws = iso(monday);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/cuisine/inventaire/state", { params: { week_start: ws } }); setSt(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }, [ws]);
  useEffect(() => { load(); }, [load]);

  if (!st) return <div className="p-8 text-muted-foreground">…</div>;
  const counts = st.counts || {};
  const prods = st.products || [];
  const byCat = (c) => prods.filter((p) => p.category === c);
  const counted = (list) => list.filter((p) => counts[p.id] !== undefined).length;
  const toOrder = prods.filter((p) => counts[p.id] && counts[p.id].qty < p.stock_min);
  const globalPct = prods.length ? Math.round((counted(prods) / prods.length) * 100) : 0;
  const lastTemp = (st.temps || [])[0];

  const saveCount = async () => {
    try {
      await api.post("/cuisine/inventaire/count", { week_start: ws, product_id: countP.id, qty, obs });
      toast.success(t("inv.counted")); setCountP(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const saveProd = async () => {
    if (!prodForm.name.trim()) return toast.error(t("inv.nameRequired"));
    try {
      if (prodForm.id) await api.put(`/cuisine/inventaire/products/${prodForm.id}`, prodForm);
      else await api.post("/cuisine/inventaire/products", { ...prodForm, category: cat || prodForm.category });
      toast.success(t("inv.prodSaved")); setProdForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const deactivate = async (p) => {
    if (!window.confirm(t("inv.confirmDeactivate", { name: p.name }))) return;
    try { await api.delete(`/cuisine/inventaire/products/${p.id}`); toast.success(t("inv.prodDeactivated")); load(); } catch { }
  };
  const confirmMove = async () => {
    try { await api.put(`/cuisine/inventaire/products/${moveAsk.p.id}`, { ...moveAsk.p, shelf: moveAsk.shelf }); toast.success(t("inv.moved")); setMoveAsk(null); load(); } catch { }
  };
  const saveTemp = async () => {
    const v = parseFloat(String(tempVal).replace(",", "."));
    if (isNaN(v)) return;
    try { const { data } = await api.post("/cuisine/inventaire/temperature", { value: v }); data.ok ? toast.success(t("inv.tempOk")) : toast.warning(t("inv.tempAlert")); setTempOpen(false); setTempVal(""); load(); } catch { }
  };
  const finish = async () => {
    const missing = prods.filter((p) => counts[p.id] === undefined).length;
    if (missing > 0 && !window.confirm(t("inv.finishWarn", { count: missing }))) return;
    try { await api.post("/cuisine/inventaire/finish", { week_start: ws }); setSummary(true); load(); } catch { }
  };
  const shift = (d) => { const x = new Date(monday); x.setDate(x.getDate() + d); setMonday(mondayOf(x)); };

  const filtered = (list) => list.filter((p) => {
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    const s = statusOf(p, counts[p.id]).k;
    return filter === "tous" || s === filter;
  });

  const Card_ = ({ p }) => {
    const c = counts[p.id]; const s = statusOf(p, c);
    return (
      <div draggable onDragStart={() => setDrag(p)} onClick={() => { setCountP(p); setQty(c ? c.qty : 0); setObs(c?.obs || ""); }}
        data-testid={`inv-prod-${p.id}`}
        className="relative w-[150px] shrink-0 rounded-2xl bg-white shadow-md ring-1 ring-slate-200 p-2 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all group">
        <button onClick={(e) => { e.stopPropagation(); setProdForm({ ...p }); }} data-testid={`inv-edit-${p.id}`}
          className="absolute top-1 right-1 h-6 w-6 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center opacity-0 group-hover:opacity-100"><MoreVertical className="h-3.5 w-3.5" /></button>
        <button onClick={(e) => { e.stopPropagation(); deactivate(p); }} data-testid={`inv-del-${p.id}`} title={t("inv.deactivate")}
          className="absolute top-1 left-1 h-6 w-6 rounded-full text-red-300 hover:bg-red-50 hover:text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 text-xs font-bold">✕</button>
        {p.image_url ? <img src={p.image_url} alt={p.name} className="h-20 w-full object-contain" /> : <div className="h-20 w-full flex items-center justify-center text-slate-300"><Boxes className="h-10 w-10" /></div>}
        <div className="text-[11px] font-bold text-slate-800 leading-tight mt-1 text-center">{p.name}</div>
        <div className="text-[9px] text-slate-500 text-center">{p.cond}</div>
        <div className="flex justify-center gap-2 text-[10px] mt-1 text-slate-600">
          <span>{t("inv.stock")}: <b>{c ? c.qty : "—"}</b></span><span>{t("inv.mini")}: <b className="text-emerald-600">{p.stock_min}</b></span>
        </div>
        <div className="mt-1 text-center rounded-md py-0.5 text-[9px] font-extrabold uppercase" style={{ color: s.col, background: s.bg }} data-testid={`inv-status-${p.id}`}>{t(`inv.status.${s.k}`)}</div>
      </div>
    );
  };

  const catView = () => {
    const cfg = CATS[cat]; const list = filtered(byCat(cat));
    const shelves = [1, 2, 3];
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setCat(null)} data-testid="inv-back"><ArrowLeft className="h-4 w-4 mr-1" /> {t("inv.back")}</Button>
          <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("inv.search")} className="pl-8 h-9 w-52" /></div>
          {["tous", "acompter", "acommander", "aumini", "rupture", "ok"].map((f) => (
            <button key={f} data-testid={`inv-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition ${filter === f ? "bg-teal-600 text-white border-teal-600" : "hover:bg-white/10"}`}>{f === "tous" ? t("inv.all") : t(`inv.status.${f}`)}</button>
          ))}
          <Button size="sm" className="ml-auto bg-teal-600 hover:bg-teal-700 text-white" data-testid="inv-add-prod" onClick={() => setProdForm({ ...emptyProd, category: cat })}><Plus className="h-4 w-4 mr-1" /> {t("inv.addProduct")}</Button>
        </div>
        {cat === "frais" && (
          <div className="flex items-center gap-3 rounded-2xl p-3 text-white" style={{ background: "linear-gradient(135deg,#0c4a6e,#0284c7)" }} data-testid="inv-temp-bar">
            <Thermometer className="h-6 w-6" />
            <div className="flex-1">
              <div className="text-xs uppercase font-bold text-white/70">{t("inv.tempTitle")}</div>
              <div className="text-2xl font-extrabold">{lastTemp ? `${lastTemp.value.toFixed(1).replace(".", ",")} °C` : "—"}
                {lastTemp && <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${lastTemp.ok ? "bg-emerald-400/30" : "bg-red-500/40"}`}>{lastTemp.ok ? t("inv.tempConform") : t("inv.tempCheck")}</span>}</div>
              <div className="text-[10px] text-white/60">{t("inv.tempNorm")}</div>
            </div>
            <Button size="sm" className="bg-white text-sky-700 hover:bg-sky-50" data-testid="inv-temp-btn" onClick={() => setTempOpen(true)}>{t("inv.tempRecord")}</Button>
          </div>
        )}
        <div className="rounded-3xl p-4 sm:p-6 space-y-1" style={{ background: cat === "frais" ? "linear-gradient(180deg,#e2e8f0,#cbd5e1)" : "linear-gradient(180deg,#7c4a21,#5c3417)", boxShadow: "inset 0 0 40px rgba(0,0,0,0.35)" }} data-testid={`inv-${cat}-view`}>
          {shelves.map((sh) => (
            <div key={sh} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag && drag.shelf !== sh) setMoveAsk({ p: drag, shelf: sh }); setDrag(null); }} data-testid={`inv-shelf-${sh}`}>
              <div className={`text-[10px] font-extrabold uppercase tracking-wider ${cat === "frais" ? "text-slate-600" : "text-amber-100/70"} px-1`}>{t("inv.shelf")} {sh}</div>
              <div className={`flex gap-3 overflow-x-auto p-3 rounded-xl min-h-[150px] ${cat === "frais" ? "bg-white/50" : "bg-black/10"}`}>
                {list.filter((p) => (p.shelf || 1) === sh).map((p) => <Card_ key={p.id} p={p} />)}
              </div>
              <div className="h-2.5 rounded-b-lg mb-2" style={{ background: cat === "frais" ? "#94a3b8" : "#3f2410" }} />
            </div>
          ))}
        </div>
      </>
    );
  };

  const home = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {["sec", "frais", "boissons"].map((c) => {
        const cfg = CATS[c]; const Icon = cfg.icon; const list = byCat(c);
        const done = counted(list); const pct = list.length ? Math.round((done / list.length) * 100) : 0;
        const ord = list.filter((p) => counts[p.id] && counts[p.id].qty < p.stock_min).length;
        return (
          <button key={c} data-testid={`inv-cat-${c}`} onClick={() => setCat(c)}
            className="rounded-3xl p-6 text-left text-white hover:scale-[1.02] transition-transform shadow-xl" style={{ background: cfg.bg }}>
            <Icon className="h-12 w-12 mb-3 opacity-90" />
            <div className="font-display text-2xl font-extrabold uppercase">{t(`inv.cat.${c}`)}</div>
            <div className="text-sm text-white/80 mt-2">{list.length} {t("inv.refs")} · {done} {t("inv.countedN")}{ord > 0 ? ` · ${ord} ${t("inv.toOrderN")}` : ""}</div>
            <div className="mt-3 h-2.5 rounded-full bg-white/20"><div className="h-2.5 rounded-full bg-white" style={{ width: `${pct}%` }} /></div>
            <div className="text-xs mt-1 text-white/80">{t("inv.progress")} {pct} %</div>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="cuisine-inventaire">
      <div className="rounded-3xl p-6 glass">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">{st.maison}</div>
            <h1 className="font-display text-3xl font-extrabold">{t("nav.cuisineInventaire")}</h1>
            <p className="text-sm text-muted-foreground" data-testid="inv-week-label">{t("inv.weekOf")} {fmt(ws)} {t("cuisine.menuWeekTo")} {fmt(iso(sunday))}{st.started_by ? ` — ${t("inv.startedBy")} ${st.started_by}` : ""}{st.finished_by ? ` · ${t("inv.finishedBy")} ${st.finished_by}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="inv-prev" onClick={() => shift(-7)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" data-testid="inv-today" onClick={() => setMonday(mondayOf(new Date()))}>{t("inv.thisWeek")}</Button>
            <Button size="sm" variant="outline" data-testid="inv-next" onClick={() => shift(7)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t("inv.globalProgress")}</span><span data-testid="inv-global-pct">{globalPct} % · {counted(prods)}/{prods.length}</span></div>
          <div className="h-3 rounded-full bg-white/10"><div className="h-3 rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all" style={{ width: `${globalPct}%` }} /></div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="inv-finish" onClick={finish}><CheckCircle2 className="h-4 w-4 mr-1.5" /> {t("inv.finish")}</Button>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="inv-summary-btn" onClick={() => setSummary(true)}><ShoppingCart className="h-4 w-4 mr-1.5" /> {t("inv.orderList")} ({toOrder.length})</Button>
        </div>
      </div>

      {cat ? catView() : home()}

      {/* Comptage */}
      <Dialog open={!!countP} onOpenChange={(o) => !o && setCountP(null)}>
        <DialogContent className="max-w-md">
          {countP && <>
            <DialogHeader><DialogTitle>{countP.name}</DialogTitle></DialogHeader>
            <div className="flex gap-4 items-center">
              {countP.image_url ? <img src={countP.image_url} alt="" className="h-24 w-24 object-contain bg-white rounded-xl p-1" /> : <div className="h-24 w-24 flex items-center justify-center bg-white rounded-xl text-slate-300"><Boxes className="h-12 w-12" /></div>}
              <div className="text-sm text-muted-foreground">{countP.cond}<br />{t("inv.mini")} : {countP.stock_min} {countP.unit}</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-bold uppercase text-muted-foreground mb-2">{t("inv.howMany")}</div>
              <div className="flex items-center justify-center gap-4">
                <Button size="icon" variant="outline" className="h-12 w-12 rounded-full" data-testid="inv-qty-minus" onClick={() => setQty(Math.max(0, qty - 1))}><Minus /></Button>
                <input data-testid="inv-qty" type="number" min="0" value={qty} onChange={(e) => setQty(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="w-24 h-14 text-center text-3xl font-extrabold rounded-xl border bg-background" />
                <Button size="icon" variant="outline" className="h-12 w-12 rounded-full" data-testid="inv-qty-plus" onClick={() => setQty(qty + 1)}><Plus /></Button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mt-3 max-w-[220px] mx-auto">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((n) => <button key={n} onClick={() => setQty(Number(String(qty === 0 ? "" : qty) + n))} className={`h-10 rounded-lg border font-bold hover:bg-white/10 ${n === 0 ? "col-start-2" : ""}`}>{n}</button>)}
              </div>
              {qty < countP.stock_min && <div className="mt-2 text-xs font-bold text-orange-500">{t("inv.willOrder", { n: Math.max(0, countP.stock_min - qty) })}</div>}
            </div>
            <div><Label className="text-xs">{t("inv.obs")}</Label>
              <select data-testid="inv-obs" className="w-full h-10 rounded-md border px-3 bg-background mt-1" value={obs} onChange={(e) => setObs(e.target.value)}>
                <option value="">—</option>{OBS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCountP(null)}>{t("planning.cancel")}</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inv-count-save" onClick={saveCount}>{t("inv.validate")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* Produit add/edit */}
      <Dialog open={!!prodForm} onOpenChange={(o) => !o && setProdForm(null)}>
        <DialogContent className="max-w-md">
          {prodForm && <>
            <DialogHeader><DialogTitle>{prodForm.id ? t("inv.editProduct") : t("inv.addProduct")}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <div><Label className="text-xs">{t("inv.prodName")}</Label><Input data-testid="inv-prod-name" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">{t("inv.cond")}</Label><Input value={prodForm.cond} onChange={(e) => setProdForm({ ...prodForm, cond: e.target.value })} /></div>
                <div><Label className="text-xs">{t("inv.unit")}</Label><Input value={prodForm.unit} onChange={(e) => setProdForm({ ...prodForm, unit: e.target.value })} /></div>
                <div><Label className="text-xs">{t("inv.stockMin")}</Label><Input data-testid="inv-prod-min" type="number" min="0" value={prodForm.stock_min} onChange={(e) => setProdForm({ ...prodForm, stock_min: parseInt(e.target.value || "0", 10) })} /></div>
                <div><Label className="text-xs">{t("inv.shelf")}</Label>
                  <select className="w-full h-10 rounded-md border px-3 bg-background" value={prodForm.shelf} onChange={(e) => setProdForm({ ...prodForm, shelf: Number(e.target.value) })}>{[1, 2, 3].map((s) => <option key={s} value={s}>{t("inv.shelf")} {s}</option>)}</select></div>
              </div>
              <div><Label className="text-xs">{t("inv.photoUrl")}</Label><Input value={prodForm.image_url} onChange={(e) => setProdForm({ ...prodForm, image_url: e.target.value })} placeholder="https://…" /></div>
              {prodForm.category === "frais" && <div><Label className="text-xs">DLC / DDM</Label><Input type="date" value={prodForm.dlc} onChange={(e) => setProdForm({ ...prodForm, dlc: e.target.value })} /></div>}
              <div><Label className="text-xs">{t("inv.notes")}</Label><Input value={prodForm.notes} onChange={(e) => setProdForm({ ...prodForm, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setProdForm(null)}>{t("planning.cancel")}</Button>
              <Button data-testid="inv-prod-save" onClick={saveProd}>{t("inv.save")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* Déplacement */}
      <Dialog open={!!moveAsk} onOpenChange={(o) => !o && setMoveAsk(null)}>
        <DialogContent className="max-w-sm">
          {moveAsk && <>
            <DialogHeader><DialogTitle>{t("inv.moveTitle")}</DialogTitle></DialogHeader>
            <p className="text-sm">{t("inv.moveAsk", { name: moveAsk.p.name, shelf: moveAsk.shelf })}</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setMoveAsk(null)}>{t("planning.cancel")}</Button>
              <Button data-testid="inv-move-confirm" onClick={confirmMove}>{t("inv.confirm")}</Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* Température */}
      <Dialog open={tempOpen} onOpenChange={setTempOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("inv.tempRecord")}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2"><Input data-testid="inv-temp-value" value={tempVal} onChange={(e) => setTempVal(e.target.value)} placeholder="3,2" className="text-2xl h-14 text-center font-bold" /><span className="text-xl font-bold">°C</span></div>
          <p className="text-xs text-muted-foreground">{t("inv.tempNorm")} · {user?.name} · {new Date().toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" })}</p>
          <DialogFooter><Button data-testid="inv-temp-save" onClick={saveTemp}>{t("inv.validate")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Résumé / commande */}
      <Dialog open={summary} onOpenChange={setSummary}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>🎉 {t("inv.summaryTitle")}</DialogTitle></DialogHeader>
          <div id="inv-print" className="space-y-2 text-sm">
            <p>{st.maison} — {t("inv.weekOf")} {fmt(ws)}</p>
            <p><b>{counted(prods)}</b> {t("inv.countedN")} / {prods.length} {t("inv.refs")} · <b className="text-orange-500">{toOrder.length}</b> {t("inv.toOrderN")} · <b className="text-red-500">{prods.filter((p) => counts[p.id]?.qty === 0).length}</b> {t("inv.status.rupture").toLowerCase()}</p>
            <div className="rounded-xl border divide-y max-h-[40vh] overflow-y-auto" data-testid="inv-order-list">
              {toOrder.length === 0 && <p className="p-3 text-muted-foreground">{t("inv.nothingToOrder")}</p>}
              {toOrder.map((p) => (
                <div key={p.id} className="flex items-center gap-2 p-2">
                  {p.image_url ? <img src={p.image_url} alt="" className="h-8 w-8 object-contain bg-white rounded" /> : <span className="h-8 w-8 flex items-center justify-center bg-white rounded text-slate-300"><Boxes className="h-4 w-4" /></span>}
                  <span className="flex-1 font-semibold">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{t("inv.stock")} {counts[p.id]?.qty} / {t("inv.mini")} {p.stock_min}</span>
                  <span className="font-bold text-orange-500">+{Math.max(0, p.stock_min - (counts[p.id]?.qty || 0))} {p.unit}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.print()} data-testid="inv-print-btn"><Printer className="h-4 w-4 mr-1.5" /> {t("cuisine.print")}</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="inv-pdf-btn" onClick={() => downloadPdf("inv-print-pdf", `inventaire-${ws}.pdf`)}>⬇ PDF</Button>
            <Button onClick={() => setSummary(false)}>{t("users.done", { defaultValue: "OK" })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfSheet id="inv-print-pdf" emoji="📦" accent="#0d9488" dark="#042f2e" maison={st.maison} title={t("inv.summaryTitle")}
        meta={[`${t("inv.weekOf")} ${fmt(ws)}`, `${prods.length} ${t("inv.refs")}`]}
        stats={[[counted(prods), t("inv.countedN"), "#0d9488"], [toOrder.length, t("inv.toOrderN"), "#ea580c"], [prods.filter((p) => counts[p.id]?.qty === 0).length, t("inv.status.rupture"), "#dc2626"]]}>
        <PdfSection title={t("inv.toOrderN")} accent="#ea580c">
          <PdfTable accent="#ea580c" headers={["☐", "Produit", "Stock", "Mini", "À commander"]} widths={[24, undefined, 55, 55, 90]}
            rows={toOrder.map((p) => ["☐", <b key="n">{p.name}</b>, counts[p.id]?.qty ?? "—", p.stock_min, <b key="o" style={{ color: "#ea580c", fontSize: 12 }}>+{Math.max(0, p.stock_min - (counts[p.id]?.qty || 0))} {p.unit}</b>])} />
        </PdfSection>
      </PdfSheet>
      <style>{`@media print { body * { visibility: hidden !important; } #inv-print, #inv-print * { visibility: visible !important; } #inv-print { position: fixed; left:0; top:0; width:100%; background:#fff; color:#111; padding:16px; } }`}</style>
    </div>
  );
}
